import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchPeople, createPerson } from '../api/people';
import type { Person, PersonType } from '../types/people';

const PERSON_TYPE: PersonType = 'external';
const TITLE = 'External';
const BREADCRUMB = 'External';

export default function PeopleExternalPage() {
  const navigate = useNavigate();
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '' });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetchPeople(PERSON_TYPE)
      .then(setPeople)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const name = addForm.name.trim();
      if (!name) {
        setAddError('Name is required');
        return;
      }
      setAddSaving(true);
      setAddError('');
      try {
        const created = await createPerson({
          name,
          email: addForm.email.trim() || null,
          person_type: PERSON_TYPE,
        });
        setPeople((prev) => [...prev, created]);
        setAddForm({ name: '', email: '' });
        setAddModalOpen(false);
        navigate(`/people/${created.id}`);
      } catch (err) {
        setAddError(err instanceof Error ? err.message : 'Failed to add person');
      } finally {
        setAddSaving(false);
      }
    },
    [addForm, navigate]
  );

  if (loading) {
    return <div>Loading…</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--color-coral)' }}>{error}</div>;
  }

  return (
    <>
      <nav style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
        <Link to="/people/founders" style={{ color: 'var(--color-blue-dark)' }}>
          People
        </Link>
        {' / '}
        <span>{BREADCRUMB}</span>
      </nav>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 8px' }}>{TITLE}</h1>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            Manage external people and view tasks assigned to each.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setAddError('');
            setAddForm({ name: '', email: '' });
            setAddModalOpen(true);
          }}
          style={{
            padding: '10px 20px',
            background: 'var(--color-blue-dark)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius)',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Add external
        </button>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
        }}
      >
        {people.map((person) => (
          <Link
            key={person.id}
            to={`/people/${person.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '20px',
              background: 'white',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              textDecoration: 'none',
              color: 'inherit',
              transition: 'box-shadow 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <img
              src={person.avatar_url ?? undefined}
              alt=""
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                objectFit: 'cover',
                background: 'var(--color-todo-bg)',
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '2px' }}>{person.name}</div>
              {person.email && (
                <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {person.email}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>

      {addModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => !addSaving && setAddModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: 400,
              boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Add external person</h2>
            <form onSubmit={handleAddSubmit}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Name *</label>
                <input
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Jane"
                  autoFocus
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Email</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="jane@example.com"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}
                />
              </div>
              {addError && <div style={{ color: 'var(--color-coral)', fontSize: '13px', marginBottom: '12px' }}>{addError}</div>}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => !addSaving && setAddModalOpen(false)}
                  style={{
                    padding: '8px 16px',
                    background: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    cursor: addSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addSaving || !addForm.name.trim()}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--color-blue-dark)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius)',
                    fontWeight: 500,
                    cursor: addSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {addSaving ? 'Adding…' : 'Add person'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
